/**
 * Steer/cancel frame types and per-session steer state (issue #2438).
 *
 * These frames ride the Explorer main-chat WebSocket as DIRECT frames (not
 * context chunks) and deliberately live outside the `ServerMessage` union in
 * api/models.ts: the union is closed over typed chunk/done/deep frames, while
 * steer statuses are a narrow control side-channel that only
 * use-websocket-message-handler consumes.
 *
 * Wire contract (server → client):
 *   {"type": "steer_ready"}                       once per turn, when steering
 *                                                 becomes available (tool loop live)
 *   {"type": "cancel_ack"}                        immediately after the server
 *                                                 accepts a cancel frame
 *   {"type": "steer_status",
 *    "data": {"steer_id", "status"}}              per steer note outcome
 *
 * Client → server (sent via the session WebSocket registry):
 *   {"type": "steer", "steer_id", "content"}      content ≤ MAX_STEER_INPUT_CHARS
 *   {"type": "cancel"}
 */

/** Frontend UX cap on steer note length. The backend enforces a higher hard
 *  cap (8000 chars) and answers over-cap notes with a `steer_status` of
 *  "rejected"; only frames beyond the transport's 32k limit are dropped
 *  without any status. */
export const MAX_STEER_INPUT_CHARS = 4000

export type SteerNoteStatus = "pending" | "delivered" | "undelivered" | "rejected"

/** One steering note chip for the running turn. `steer_id` is client-generated. */
export type SteerNote = {
  steer_id: string
  content: string
  status: SteerNoteStatus
}

/** Sub-toggles of the `explorer_steer` system feature (admin-configurable).
 *  Keys mirror the backend config; absent keys default to enabled — but only
 *  when the feature flag itself is on (getFeatureConfig returns undefined for
 *  disabled features, and callers must gate on the flag first). */
export type ExplorerSteerFeatureConfig = {
  inject_enabled?: boolean
  stop_redirect_enabled?: boolean
}

export type SteerReadyFrame = { type: "steer_ready" }
export type CancelAckFrame = { type: "cancel_ack" }
export type SteerStatusFrame = {
  type: "steer_status"
  data: { steer_id: string; status: Exclude<SteerNoteStatus, "pending"> }
}

export type SteerServerFrame = SteerReadyFrame | CancelAckFrame | SteerStatusFrame

/**
 * Defensive narrower for inbound WS frames. Returns a validated frame object
 * or null — used instead of a type-predicate guard because these types never
 * overlap the closed `ServerMessage` union.
 */
export function asSteerServerFrame(message: unknown): SteerServerFrame | null {
  if (!message || typeof message !== "object" || !("type" in message)) {
    return null
  }
  const type = (message as { type?: unknown }).type
  if (type === "steer_ready") {
    return { type: "steer_ready" }
  }
  if (type === "cancel_ack") {
    return { type: "cancel_ack" }
  }
  if (type === "steer_status") {
    const data = (message as { data?: unknown }).data
    if (!data || typeof data !== "object") {
      return null
    }
    const steerId = (data as { steer_id?: unknown }).steer_id
    const status = (data as { status?: unknown }).status
    if (
      typeof steerId !== "string" ||
      steerId.length === 0 ||
      (status !== "delivered" && status !== "undelivered" && status !== "rejected")
    ) {
      return null
    }
    return { type: "steer_status", data: { steer_id: steerId, status } }
  }
  return null
}
